import { CreateCreditApplicationInput } from '@trnsact/trnsact-shared-types';
import { formatOcaFieldValue, getOcaFieldSelectOptions } from 'utils/ocaFieldsCatalog';
import { ExtractionFieldNode, ExtractionRecord, FieldEditGroup, FieldEditState, FieldSelectOption } from '../types';

// Field key is typed against CreateCreditApplicationInput for refactor safety
export type ExtractionKey = keyof CreateCreditApplicationInput;

// Bookkeeping/system-linkage fields and structural arrays from the extraction schema
// (see credit-app-parsing/AI_CREDIT_APP.md, "Bookkeeping/meta") — these are set by app/CRM logic
// elsewhere in the flow (dealer resolution, account linkage, form wiring), never meant to be
// reviewer-editable free text, so they must never fall into the "Additional Extracted Fields"
// catch-all even if the AI extraction happens to include them.
export const NON_REVIEWABLE_EXTRACTION_FIELDS = new Set<ExtractionKey>([
  'owners',
  'references',
  'applicationType',
  'dcrType',
  'formId',
  'draft',
  'source',
  'entityType',
  'vendorGUID',
  'repId',
  'lenderProfileId',
  'dynamicsContactId',
  'transactionGUID',
  'partnerId',
  'partnerDealerId',
]);

export interface ExtractionFieldConfig {
  label: string;
  key: ExtractionKey;
  // Only needed to override the OCA fields catalog (e.g. fields not captured by the OCA form)
  options?: FieldSelectOption[];
}

// Dynamics CRM optionset codes for tib (time in business) — not part of the OCA form, so it can't
// be derived from fields_sectionized.json; must stay in sync with the CRM field
export const TIME_IN_BUSINESS_OPTIONS: FieldSelectOption[] = [
  { label: 'Pre-Startup', value: '803370000' },
  { label: '0-6 Months', value: '803370001' },
  { label: '6-12 Months', value: '803370002' },
  { label: '1-2 Years', value: '803370003' },
  { label: '2-3 Years', value: '803370004' },
  { label: '3-5 Years', value: '803370005' },
  { label: '5-10 Years', value: '803370006' },
  { label: '10+ Years', value: '803370007' },
];

export interface ExtractionFieldGroupConfig {
  groupLabel: string;
  fields: ExtractionFieldConfig[];
}

// Groups mirror the ticket's "personal identity / financial information / general financial
// background" breakdown. Labels sourced from DynamicOCA/config/fields_sectionized.json displayLabel.en
export const EXTRACTION_FIELD_GROUPS: ExtractionFieldGroupConfig[] = [
  {
    groupLabel: 'Applicant Information',
    fields: [
      { label: 'Legal Entity Name', key: 'businessName' },
      { label: 'DBA', key: 'dba' },
      { label: 'First Name', key: 'firstName' },
      { label: 'Last Name', key: 'lastName' },
      { label: 'Email', key: 'email' },
      { label: 'Phone Number', key: 'phone' },
      { label: 'Business Phone', key: 'bizPhone' },
      { label: 'Physical Business Address', key: 'address' },
      { label: 'City', key: 'city' },
      { label: 'State', key: 'state' },
      { label: 'Postal Code', key: 'postalCode' },
      { label: 'County', key: 'addressCounty' },
      { label: 'Country', key: 'country' },
    ],
  },
  {
    groupLabel: 'Financial Information',
    fields: [
      { label: 'Equipment Financing Amount Requested', key: 'amountRequested' },
      { label: 'Annual Revenue', key: 'annualRevenue' },
      { label: 'Term Requested', key: 'termRequested' },
    ],
  },
  {
    groupLabel: 'Business Background',
    fields: [
      { label: 'Legal Structure', key: 'legalStructure' },
      { label: 'Business Description', key: 'businessType' },
      { label: 'Time in Business', key: 'tib', options: TIME_IN_BUSINESS_OPTIONS },
      { label: 'Number of Employees', key: 'numOfEmployees' },
      { label: 'Equipment Make and Model', key: 'ccg_transaction_equipmentdescription' },
    ],
  },
];

export function confidenceColor(score: number): string {
  if (score >= 0.85) return '#4caf50';
  if (score >= 0.65) return '#ffa726';
  return '#ef5350';
}

function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/^./, c => c.toUpperCase());
}

function buildFieldState(
  key: string,
  label: string,
  node: ExtractionFieldNode | undefined,
  edits: Record<string, string>,
  overrideOptions?: FieldSelectOption[]
): FieldEditState {
  // Select-backed fields carry the raw CRM/OCA option value — the label lookup is left to the Select
  const options = overrideOptions ?? getOcaFieldSelectOptions(key);
  const originalValue = node?.value == null ? '' : options ? String(node.value) : formatOcaFieldValue(key, node.value);
  const currentValue = edits[key] ?? originalValue;
  return {
    key,
    label,
    originalValue,
    currentValue,
    confidence: node?.confidenceScore ?? null,
    isDirty: currentValue !== originalValue,
    options,
  };
}

// Groups configured fields first, then buckets any remaining extraction keys (forward-compatible
// with the larger lender-side field set) into a catch-all group instead of dropping them.
export function buildFieldEditGroups(
  extraction: ExtractionRecord | null,
  edits: Record<string, string>
): FieldEditGroup[] {
  if (!extraction) return [];

  const usedKeys = new Set<string>();
  const groups = EXTRACTION_FIELD_GROUPS.map(({ groupLabel, fields }) => ({
    groupLabel,
    fields: fields.map(({ label, key, options }) => {
      usedKeys.add(key);
      return buildFieldState(key, label, extraction[key], edits, options);
    }),
  }));

  const overflowKeys = Object.keys(extraction).filter(
    key => !NON_REVIEWABLE_EXTRACTION_FIELDS.has(key as ExtractionKey) && !usedKeys.has(key)
  );
  if (overflowKeys.length > 0) {
    groups.push({
      groupLabel: 'Additional Extracted Fields',
      fields: overflowKeys.map(key => buildFieldState(key, humanizeKey(key), extraction[key], edits)),
    });
  }

  return groups;
}
